import React from 'react';
import PropTypes from 'prop-types';
import _isEmpty from 'lodash/isEmpty';
import { Field, FieldArray } from 'redux-form';
import LastUpdate from 'components/LastUpdate';
import FooterForm from 'features/ManageServices/FooterForm/FooterFormContainer';

import { manualUserVenuesPropTypes, msiLastChangePropTypes } from 'constants/propTypes';
import { Input, InputLabel, PhoneNumberInput, Textarea } from 'components/Form';
import Message from 'components/Message';
import { BackNavLink, Title } from 'features/App';

import InstructorFormAvatar from './InstructorFormComponents/InstructorFormAvatar';
import InstructorFormVenueDropdown from './InstructorFormComponents/InstructorFormVenueDropdown';
import InstructorFormSocialMedia from './InstructorFormComponents/InstructorFormSocialMedia';
import { messages } from '../Instructors.messages';

import './InstructorForm.scss';

export const InstructorFormFields = ({ pictureUrl, venues, countryCodeShort }) => (
  <div className="grid">
    <Field name="picture" pictureUrl={pictureUrl} component={InstructorFormAvatar} />
    <div className="grid__item md-1/2 mb-large">
      <div data-cypress="first-name-input">
        <Field id="firstName" name="firstName" type="text" component={Input} label={messages.form.firstNameLabel} />
      </div>
      <Field id="lastName" name="lastName" type="text" component={Input} label={messages.form.lastNameLabel} optional />
      <Field id="email" name="email" type="email" component={Input} label={messages.form.emailAddressLabel} optional />
      <div data-cypress="phone-input">
        <Field
          id="phone"
          name="phone"
          component={PhoneNumberInput}
          defaultCountry={countryCodeShort}
          label={messages.form.phoneNumberLabel}
          optional
        />
      </div>
      {!_isEmpty(venues) && (
        <div className="mb-large">
          <InputLabel message={messages.form.venueLocationsLabel} />
          <Field id="venue-location" name="venueIds" venues={venues} component={InstructorFormVenueDropdown} />
        </div>
      )}
    </div>
    <div className="grid__item md-1/2 mb-large">
      <Field
        id="bio"
        name="bio"
        inputClassName="instructor-form__textarea-box"
        component={Textarea}
        label={messages.form.bioLabel}
        optional
      />
      <Field
        id="certifications"
        name="certifications"
        className="mb-large"
        inputClassName="instructor-form__textarea-box"
        component={Textarea}
        label={messages.form.certificationsLabel}
        optional
      />
      <FieldArray name="socialHandles" component={InstructorFormSocialMedia} />
    </div>
  </div>
);

InstructorFormFields.propTypes = {
  countryCodeShort: PropTypes.string,
  pictureUrl: PropTypes.string,
  venues: PropTypes.arrayOf(
    PropTypes.shape({
      venue_id: PropTypes.number,
      name: PropTypes.string,
    })
  ),
};

export const InstructorFormFooter = ({
  handleDelete,
  lastChange,
  showDeleteButton,
  submitting,
  formDirty,
  onCancel,
}) => {
  const handleClickCancel = () => {
    if (onCancel) {
      onCancel();
    }
  };

  return (
    <>
      <LastUpdate {...lastChange} />
      <FooterForm
        deleteMessage={messages.form.delete}
        submitMessage={messages.form.save}
        formName="instructorForm"
        visible
        submitDisabled={!formDirty}
        submitting={submitting}
        showDelete={showDeleteButton}
        onClickCancel={handleClickCancel}
        onClickDelete={handleDelete}
      />
    </>
  );
};

InstructorFormFooter.propTypes = {
  handleDelete: PropTypes.func,
  lastChange: msiLastChangePropTypes,
  showDeleteButton: PropTypes.bool,
  submitting: PropTypes.bool,
  formDirty: PropTypes.bool,
  onCancel: PropTypes.func,
};

const InstructorForm = ({
  handleSubmit,
  handleDelete,
  instructor,
  venues,
  countryCodeShort,
  showDeleteButton = false,
  submitting = false,
  dirty = false,
  onCancel,
}) => {
  const { id, pictureUrl, lastChange } = instructor;

  return (
    <form id="instructorForm" className="instructor-form" onSubmit={handleSubmit}>
      <BackNavLink useHistoryHook />
      <Message as={Title} message={id ? messages.form.editInstructorTitle : messages.form.addInstructorTitle} />
      <div className="instructor-form__content">
        <InstructorFormFields venues={venues} pictureUrl={pictureUrl} id={id} countryCodeShort={countryCodeShort} />
        <InstructorFormFooter
          handleDelete={handleDelete}
          lastChange={lastChange}
          showDeleteButton={showDeleteButton}
          submitting={submitting}
          formDirty={dirty}
          onCancel={onCancel}
        />
      </div>
    </form>
  );
};

InstructorForm.propTypes = {
  handleSubmit: PropTypes.func,
  handleDelete: PropTypes.func,
  instructor: PropTypes.shape({
    id: PropTypes.number,
    firstName: PropTypes.string,
    lastName: PropTypes.string,
    phone: PropTypes.string,
    pictureUrl: PropTypes.string,
    venues: PropTypes.arrayOf(
      PropTypes.shape({
        venue_id: PropTypes.number,
        name: PropTypes.string,
      })
    ),
    email: PropTypes.string,
    bio: PropTypes.string,
    certifications: PropTypes.string,
    lastChange: msiLastChangePropTypes,
  }),
  venues: manualUserVenuesPropTypes,
  initialValues: PropTypes.shape({}),
  countryCodeShort: PropTypes.string,
  showDeleteButton: PropTypes.bool,
  submitting: PropTypes.bool,
  dirty: PropTypes.bool,
  onCancel: PropTypes.func,
};

export default InstructorForm;
